import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { adminApi } from '../../lib/api';

const EMPTY_FORM = {
  title: '',
  slug: '',
  summary: '',
  coverImageUrl: '',
  coverImageAlt: '',
  author: 'Al-Amen Technology',
  category: 'News',
  bodyMarkdown: '',
  isFestival: false,
  eventName: '',
  eventStart: '',
  eventEnd: '',
  eventTimezone: 'Asia/Dubai',
  eventStand: '',
  seoTitle: '',
  seoDescription: '',
  status: 'draft'
};

function slugify(input) {
  return String(input || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function AdminArticles({ token }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const slugTouched = useRef(false);

  const refresh = () => {
    setLoading(true);
    adminApi
      .listArticles(token)
      .then((data) => setArticles(data.articles || []))
      .catch(() => setNotice('Failed to load articles.'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [token]);

  const startEdit = (article) => {
    setEditingId(article.id);
    setForm({ ...EMPTY_FORM, ...article });
    slugTouched.current = true;
    setErrors([]);
  };

  const startNew = () => {
    setEditingId('new');
    setForm(EMPTY_FORM);
    slugTouched.current = false;
    setErrors([]);
  };

  const cancel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors([]);
  };

  const onTitleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      title: value,
      slug: slugTouched.current ? prev.slug : slugify(value)
    }));
  };

  const onUploadCover = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await adminApi.uploadImage(token, file);
      setForm((prev) => ({ ...prev, coverImageUrl: url }));
    } catch (err) {
      setNotice(err.message);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      if (editingId === 'new') {
        await adminApi.createArticle(token, form);
        setNotice('Article created.');
      } else {
        await adminApi.updateArticle(token, editingId, form);
        setNotice('Article updated.');
      }
      cancel();
      refresh();
    } catch (err) {
      setErrors(err.data?.errors || [err.message]);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteArticle(token, article.id);
      refresh();
    } catch (err) {
      setNotice(err.message);
    }
  };

  return (
    <Box>
      {notice ? (
        <Alert severity="info" onClose={() => setNotice('')} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      ) : null}

      {editingId ? (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editingId === 'new' ? 'Add article' : 'Edit article'}
          </Typography>
          {errors.length ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.map((e, i) => (
                  <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>
                ))}
              </ul>
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <TextField label="Title" fullWidth value={form.title} onChange={(e) => onTitleChange(e.target.value)} sx={{ mb: 2 }} />
              <TextField
                label="Slug"
                fullWidth
                value={form.slug}
                onChange={(e) => {
                  slugTouched.current = true;
                  setForm({ ...form, slug: slugify(e.target.value) });
                }}
                helperText={`Published at /blog/${form.slug || '…'}`}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Summary"
                fullWidth
                multiline
                minRows={2}
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField label="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
                <TextField label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <TextField label="Cover image URL" fullWidth value={form.coverImageUrl} onChange={(e) => setForm({ ...form, coverImageUrl: e.target.value })} />
                <Button component="label" variant="outlined" disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload'}
                  <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={onUploadCover} />
                </Button>
              </Stack>
              <TextField
                label="Cover image alt text"
                fullWidth
                value={form.coverImageAlt}
                onChange={(e) => setForm({ ...form, coverImageAlt: e.target.value })}
                sx={{ mb: 2 }}
              />

              <TextField
                label="Body (Markdown)"
                fullWidth
                multiline
                minRows={10}
                value={form.bodyMarkdown}
                onChange={(e) => setForm({ ...form, bodyMarkdown: e.target.value })}
                sx={{ mb: 2, fontFamily: 'monospace' }}
              />

              <FormControlLabel
                control={<Checkbox checked={!!form.isFestival} onChange={(e) => setForm({ ...form, isFestival: e.target.checked })} />}
                label="This is a festival / event article"
              />
              {form.isFestival ? (
                <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Event name" fullWidth value={form.eventName} onChange={(e) => setForm({ ...form, eventName: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField label="Start date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.eventStart} onChange={(e) => setForm({ ...form, eventStart: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField label="End date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.eventEnd} onChange={(e) => setForm({ ...form, eventEnd: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <TextField label="Stand / booth" fullWidth value={form.eventStand} onChange={(e) => setForm({ ...form, eventStand: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <TextField label="Timezone" fullWidth value={form.eventTimezone} onChange={(e) => setForm({ ...form, eventTimezone: e.target.value })} />
                  </Grid>
                </Grid>
              ) : null}

              <TextField label="SEO title" fullWidth value={form.seoTitle} onChange={(e) => setForm({ ...form, seoTitle: e.target.value })} sx={{ mb: 2 }} />
              <TextField label="SEO description" fullWidth value={form.seoDescription} onChange={(e) => setForm({ ...form, seoDescription: e.target.value })} sx={{ mb: 2 }} />

              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} size="small">
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                Preview
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 600, overflow: 'auto' }}>
                <Typography variant="h6">{form.title || 'Untitled'}</Typography>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.bodyMarkdown || '*Nothing written yet.*'}</ReactMarkdown>
              </Paper>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="text" onClick={cancel}>
              Cancel
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Button variant="contained" onClick={startNew} sx={{ mb: 3 }}>
          Add article
        </Button>
      )}

      {loading ? (
        <Typography sx={{ color: 'text.secondary' }}>Loading…</Typography>
      ) : (
        <Stack spacing={1.5}>
          {articles.map((article) => (
            <Paper key={article.id} variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography fontWeight={700}>{article.title}</Typography>
                  <Chip size="small" label={article.status} color={article.status === 'published' ? 'success' : 'default'} />
                  {article.isFestival ? <Chip size="small" label="Event" /> : null}
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  /blog/{article.slug}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => startEdit(article)}>
                  Edit
                </Button>
                <Button size="small" color="error" onClick={() => remove(article)}>
                  Delete
                </Button>
              </Stack>
            </Paper>
          ))}
          {articles.length === 0 ? <Typography sx={{ color: 'text.secondary' }}>No articles yet.</Typography> : null}
        </Stack>
      )}
    </Box>
  );
}
